import { BarChart } from '../../../../../../components/Charts/BarChart'
import { BoxChart } from '../../../../../../components/Charts/BoxChart'
import { GroupByTime, UUID } from '@indigohive/cogfy-types'
import { formatCurrency } from '../../format-currency'
import { formatTasks } from '../../format-tasks'
import { generateColorFromId } from './generate-color-from-id'
import { HeaderComponent } from '../HeaderComponent'
import { htmlLegendPlugin } from './html-legend-plugin'
import { Skeleton } from '../../../../../../components'
import { TooltipItem } from 'chart.js'
import { useCogfy } from '../../../../../../hooks'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export type ConnectionsUsagesChartProps = {
  startDate?: Date
  endDate?: Date
  collectionId?: string
  provider?: string
}

export function ConnectionsUsagesChart (props: ConnectionsUsagesChartProps) {
  const { startDate, endDate, collectionId, provider } = props

  const cogfy = useCogfy()
  const { t } = useTranslation()
  const [groupBy, setGroupBy] = useState<GroupByTime>('hour')

  const query = {
    groupBy,
    startDate: startDate?.toISOString(),
    endDate: endDate?.toISOString(),
    timezoneOffset: new Date().getTimezoneOffset() / -60,
    collectionId: collectionId as UUID,
    provider
  }

  const getConnectionsUsages = useQuery({
    queryKey: ['getConnectionUsages', query] as const,
    queryFn: ({ signal }) => cogfy.getConnectionsUsages(query, { signal })
  })

  const totalsMap = new Map<string, number>()

  getConnectionsUsages.data?.forEach(({ currency, totalCosts }) => {
    const currentTotal = totalsMap.get(currency) ?? 0
    totalsMap.set(currency, currentTotal + (totalCosts || 0))
  })

  const totalUSD = formatCurrency('USD', totalsMap.get('USD') ?? 0)
  const totalBRL = formatCurrency('BRL', totalsMap.get('BRL') ?? 0)
  const totalEUR = formatCurrency('EUR', totalsMap.get('EUR') ?? 0)

  const data = {
    labels: Array.from(new Set(getConnectionsUsages.data?.flatMap(item => item.dataByTime.map(d => d.date)))),
    datasets: getConnectionsUsages.data
      ?.map(item => ({
        label: item.connection.name,
        data: item.dataByTime.map(d => d.costs),
        tasks: item.dataByTime.map(d => d.tasks),
        backgroundColor: generateColorFromId(item.connection.id),
        stacked: 'true',
        currency: item.currency,
        provider: item.connection.provider,
        totalCosts: item.totalCosts,
        totalTasks: item.totalTasks,
        deleted: item.connection.deleted
      }))
      .filter(usage => usage !== undefined)
      .sort((a, b) => {
        if (a.deleted !== b.deleted) return Number(a.deleted) - Number(b.deleted) // não deletados antes
        return a.label.localeCompare(b.label) // ordena alfabeticamente, considerando acentuação
      })
  }

  const loading = getConnectionsUsages.isLoading

  return (
    <BoxChart
      title={t('workspaceUsagePage:Connections usages by period')}
      headerComponent={
        <HeaderComponent
          groupBy={groupBy}
          setGroupBy={setGroupBy}
        />
      }
      aside={
        <>
          <p className="text-xl">{t('workspaceUsagePage:Costs')}</p>
          {loading && <AsideLoading />}
          {!loading && (
            <>
              <p className="text-2xl font-semibold">{totalUSD}</p>
              <p className="text-md opacity-80 font-semibold">{totalBRL}</p>
              <p className="text-md opacity-80 font-semibold">{totalEUR}</p>
              <div id="legend-container" className="mt-6 h-full overflow-y-auto" style={{ scrollbarWidth: 'thin' }} />
            </>
          )}
        </>
      }
    >
      <BarChart
        className="min-h-[400px]"
        data={data}
        options={{
          maintainAspectRatio: false,
          responsive: true,
          plugins: {
            legend: { display: false },
            htmlLegend: { containerID: 'legend-container' },
            tooltip: {
              mode: 'index',
              callbacks: {
                label: (tooltip) => {
                  if (tooltip.raw === 0) return ''
                  return tooltip.dataset.label?.split('¨')?.at(0) + '       ' + formatCurrency('USD', tooltip.parsed.y)
                },
                afterLabel: (tooltip: TooltipItem<'bar'> & { dataset: { tasks: number [] } }) => {
                  if (tooltip.raw === 0) return ''
                  return 'Tasks: ' + formatTasks(tooltip.dataset.tasks[tooltip.dataIndex], 'standard') + '\n'
                },
                footer: (tooltipItems) => {
                  const dataIndex = tooltipItems.at(0)?.dataIndex ?? 0
                  const totals = { USD: 0, BRL: 0, EUR: 0 }
                  const currencies = Object.keys(totals)

                  data.datasets?.forEach(dataset => {
                    const value = dataset.data[dataIndex]
                    if (typeof value === 'number' && currencies.includes(dataset.currency)) {
                      totals[dataset.currency as 'USD' | 'BRL' | 'EUR'] += value
                    }
                  })

                  const result = []

                  if (totals.USD !== 0) result.push(`Total: ${formatCurrency('USD', totals.USD)}`)
                  if (totals.BRL !== 0) result.push(`Total: ${formatCurrency('BRL', totals.BRL)}`)
                  if (totals.EUR !== 0) result.push(`Total: ${formatCurrency('EUR', totals.EUR)}`)

                  return result.join('\n')
                }

              }
            }
          },
          scales: {
            x: {
              stacked: true,
              grid: { color: 'transparent' }
            },
            y: { stacked: true }
          }
        }}
        plugins={[htmlLegendPlugin]}
        loading={loading}
        error={getConnectionsUsages.isError}
      />
    </BoxChart>
  )
}

function AsideLoading () {
  return (
    <div className="flex flex-col gap-2 mt-2">
      <Skeleton className="w-28 h-8" />
      <Skeleton className="w-16 h-4" />
      <Skeleton className="w-16 h-4" />
      <Skeleton className="w-16 h-4" />
    </div>
  )
}
